import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router"
import { ThemeProvider } from "@/src/components/ThemeProvider"
import { Toaster } from "@/src/components/ui/sonner"
import { Header } from "@/src/components/layout/Header"
import { Footer } from "@/src/components/layout/Footer"
import { I18nProvider } from "@/src/components/I18nInitializer"
import { QueryProvider } from "@/src/components/QueryProvider"
import { PostHogProvider } from "@/src/components/PostHogProvider"
import { PublicConfigProvider } from "@/src/lib/publicConfig"
import { AuthProvider } from "@/src/components/AuthContextProvider"
import { ErrorBoundary } from "@/src/components/ErrorBoundary"
import { SomethingWentWrong } from "@/src/components/SomethingWentWrong"
import { NotFound } from "@/src/components/NotFound"
import { NotificationProvider } from "@/src/components/ui/notification"
import { PWAUpdatePrompt } from "@/src/components/ui/pwa-update-prompt"
import appCss from "@/src/styles/globals.css?url"

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover",
      },
      { title: "GearLens" },
      { name: "description", content: "Analyze technical drawings of gears and tools." },
      { name: "theme-color", content: "#0ea5e9" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "GearLens" },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
      {
        rel: 'icon',
        href: '/favicon.svg',
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/icons/icon-192x192.png',
      },
      // Manifest is excluded in development because:
      // 1. vite-plugin-pwa devOptions.enabled is set to false (vite.config.ts line 199)
      // 2. PWA service worker and manifest are not generated during development
      // 3. This avoids browser warnings about missing manifest file
      // For testing PWA features locally, set devOptions.enabled: true in vite.config.ts
      ...(import.meta.env.DEV
        ? []
        : [
            {
              rel: 'manifest',
              href: '/manifest.webmanifest',
            },
          ]),
    ],
  }),
  component: RootLayout,
  errorComponent: ({ error }) => {
    // Capture router-level errors in PostHog (production only)
    if (typeof window !== 'undefined') {
      const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
      
      if (!isDevelopment) {
        try {
          const posthog = (window as any).posthog;
          if (posthog?.capture) {
            posthog.capture('$exception', {
              $exception_type: error?.name || 'RouterError',
              $exception_message: error?.message || String(error),
              $exception_stack_trace: error?.stack,
              $exception_source: 'router_error',
              timestamp: new Date().toISOString(),
            });
          }
        } catch (posthogError) {
          console.error('Failed to capture router error in PostHog:', posthogError);
        }
      } else {
        console.log('[PostHog] Router error not sent (development mode)');
      }
    }

    return (
      <I18nProvider>
        <QueryProvider>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange
          >
            <SomethingWentWrong error={error} showLayout={false} />
          </ThemeProvider>
        </QueryProvider>
      </I18nProvider>
    )
  },
  notFoundComponent: NotFound,
})

function RootLayout() {
  return (
    <html lang="en" suppressHydrationWarning={true} className="overscroll-none">
      <head>
        <HeadContent />
      </head>
      <body>
        <AuthProvider>
          <PublicConfigProvider>
            <PostHogProvider>
              <ErrorBoundary>
                <I18nProvider>
                  <QueryProvider>
                    <ThemeProvider
                      attribute="class"
                      defaultTheme="system"
                      enableSystem
                      disableTransitionOnChange
                    >
                      <NotificationProvider>
                        <LayoutContent />
                        <PWAUpdatePrompt />
                        <Toaster
                          position="top-center"
                          duration={4000}
                          style={{ zIndex: 9999 }}
                        />
                      </NotificationProvider>
                    </ThemeProvider>
                  </QueryProvider>
                </I18nProvider>
              </ErrorBoundary>
            </PostHogProvider>
          </PublicConfigProvider>
        </AuthProvider>
        <Scripts />
      </body>
    </html>
  )
}

function LayoutContent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isLandingPage = pathname === "/"

  return (
    <>
      <div className="flex flex-col min-h-screen w-full max-w-448 mx-auto px-4 scrollbar-gutter-stable">
        <Header />
        <main className="grow">
          <Outlet />
        </main>
        {!isLandingPage && <Footer />}
      </div>
    </>
  )
}
